import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo
} from "react";

import { Link } from "@chakra-ui/react";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

import { NamedLink } from "@/components/common/navigation/NamedLink";
import {
  ColumnDefinition,
  DataTable,
  DataTableProps
} from "@/components/common/table/DataTable";
import { EntityCrudActions } from "@/components/data/EntityCrudActions";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import { CrudRouteType, getCrudRouteName, getIdParamName } from "@/routes/crud";
import { getAccessorValue } from "@/util/data";

import { useCrudListContext } from "./CrudListContext";
import { CrudListEmptyTableContent } from "./CrudListEmptyTableContent";

export type CrudListTableProps<E extends Entity = Entity> = Omit<
  DataTableProps<E>,
  | "columnDefinitions"
  | "emptyContent"
  | "idAccessor"
  | "isLoading"
  | "items"
  | "onSelectedItemsChange"
> & {
  addActions?: boolean;
  showEditButton?: boolean;
  columns: Array<ColumnDefinition<E>>;
  onSelectedItemsChange?: (
    newSelectedEntityIds: Array<string>,
    newSelectedEntities: Array<E>
  ) => void;
  onRowItemClick?: (item: E) => void;
};

const NON_EDITABLE_TRANSACTION_STATUSES = ["DELETED", "PENDING", "PUBLISHING"];

export function CrudListTable<E extends Entity>({
  addActions = true,
  showEditButton = false,
  columns,
  onSelectedItemsChange,
  onRowItemClick,
  ...tableProps
}: PropsWithChildren<CrudListTableProps<E>>): ReactElement<any, any> | null {
  const {
    allEntities,
    currentPageEntities,
    dataType,
    detailsRouteName,
    editRouteName,
    isItemDeletable,
    isItemEditable,
    isManualLoading,
    onDelete,
    sortCriteria,
    sortList
  } = useCrudListContext<E>();

  const navigate = useNamedNavigate();

  const handleRowClick = useCallback(
    (item: E) => {
      if (onRowItemClick) {
        onRowItemClick(item);
        return;
      }
      if (!showEditButton && editRouteName) {
        if (dataType === DataType.Transaction) {
          const transaction = item as unknown as Transaction;
          if (NON_EDITABLE_TRANSACTION_STATUSES.includes(transaction.status)) {
            return;
          }
        }

        navigate(getCrudRouteName(dataType, CrudRouteType.Edit), {
          transactionId: item.id
        });
      }
    },
    [editRouteName, dataType, navigate, showEditButton, onRowItemClick]
  );

  const isRowClickable = useCallback(
    (item: E) => {
      if (onRowItemClick) {
        return true;
      }
      if (!editRouteName || showEditButton) {
        return false;
      }

      if (dataType === DataType.Transaction) {
        const transaction = item as unknown as Transaction;
        return !NON_EDITABLE_TRANSACTION_STATUSES.includes(transaction.status);
      }

      return true;
    },
    [dataType, editRouteName, showEditButton, onRowItemClick]
  );

  const columnsWithActions = useMemo<Array<ColumnDefinition<E>>>(() => {
    if (columns.length === 0) {
      return columns;
    }

    const newColumns = [...columns];

    // Only add clickable link to first column if we're showing details and not using row click for edit
    if (detailsRouteName && (showEditButton || !editRouteName)) {
      const originalFirstColumn = {
        ...newColumns[0]
      };
      newColumns[0] = {
        ...originalFirstColumn,
        accessor: (item) => {
          const firstColumnValue = getAccessorValue(
            item,
            originalFirstColumn.accessor
          );
          const linkParams = {
            [getIdParamName(dataType)]: item.id
          };

          return (
            <Link as={NamedLink} name={detailsRouteName} params={linkParams}>
              {firstColumnValue as React.ReactNode}
            </Link>
          );
        }
      };
    }

    // Add actions column if needed
    const canAddActions = (showEditButton && editRouteName) || onDelete;
    if (addActions && canAddActions) {
      newColumns.push({
        id: "list-actions-column",
        header: "",
        accessor: (item) => (
          <EntityCrudActions<E>
            dataType={dataType!}
            item={item}
            editRouteName={showEditButton ? editRouteName : undefined}
            onDelete={onDelete}
            canDelete={isItemDeletable}
            canEdit={isItemEditable}
          />
        )
      });
    }

    return newColumns;
  }, [
    addActions,
    columns,
    dataType,
    detailsRouteName,
    editRouteName,
    isItemDeletable,
    isItemEditable,
    onDelete,
    showEditButton
  ]);

  const handleSelectedItemsChange = useCallback(
    (newSelectedEntityIds: Array<string>) => {
      if (onSelectedItemsChange) {
        const newSelectedEntities = allEntities.filter((entity) =>
          newSelectedEntityIds.includes(entity.id)
        );
        onSelectedItemsChange(newSelectedEntityIds, newSelectedEntities);
      }
    },
    [allEntities, onSelectedItemsChange]
  );

  const handleSort = useCallback(
    (newSortBy: string, descending: boolean) => {
      sortList({
        sortBy: newSortBy,
        sortOrder: descending ? "DESC" : "ASC"
      });
    },
    [sortList]
  );

  return (
    <DataTable<E>
      columnDefinitions={columnsWithActions}
      idAccessor="id"
      isLoading={isManualLoading}
      items={currentPageEntities}
      onSelectedItemsChange={handleSelectedItemsChange}
      onSortChange={handleSort}
      sortColumn={sortCriteria?.sortBy}
      sortDescending={sortCriteria?.sortOrder === "DESC"}
      emptyContent={<CrudListEmptyTableContent />}
      onRowClick={handleRowClick}
      isRowClickable={isRowClickable}
      {...tableProps}
    />
  );
}
